import { Box, Tooltip, Typography } from '@mui/material';
import { flatten, isEmpty } from 'lodash';
import React from 'react';
import { COLOR_BY_LEVEL, Level } from '../utils/utils';

export type Segment = {
  period?: Date;
  cells: CellData[];
};

export type CellData = {
  level: Level;
  label: string;
  tooltip?: React.ReactNode;
};

export const NO_DATA_CELL: CellData = {
  label: 'No Data',
  level: 'NA',
};

export const NA_CELL: CellData = {
  label: 'N/A',
  level: 'NA',
};

export const MIN_SEGMENT_WIDTH = 350;

const isSingleCell = (segments: Segment[]) => {
  return segments.length === 1 && segments[0].cells.length === 1;
};

const generateGradientBackgroundProgressBar = (segments: Segment[]) => {
  const segmentLength = 100 / segments.length;
  const gradientColors = segments
    .map((segment, segmentIdx) => {
      const cells = segment.cells;
      const cellLength = segmentLength / cells.length;
      const gradientCells = cells.map((cell, index) => {
        const color = COLOR_BY_LEVEL[cell.level];
        const colorPosition = Math.round(
          segmentIdx * segmentLength + cellLength * (index + 0.5),
        );
        return [`${color} ${colorPosition}%`];
      });
      return flatten(gradientCells);
    })
    .join(', ');

  return `linear-gradient(90deg, ${gradientColors});`;
};

type ProgressBarProps = {
  segments: Segment[];
};
export const ProgressBar = ({ segments }: ProgressBarProps) => {
  if (isEmpty(segments)) {
    return null;
  }
  const backgroundColor = isSingleCell(segments)
    ? COLOR_BY_LEVEL[segments[0].cells[0].level]
    : generateGradientBackgroundProgressBar(segments);

  return (
    <Box
      sx={{
        display: 'flex',
        background: backgroundColor,
      }}
    >
      {segments.map((segment, idx) => {
        return (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              minWidth: MIN_SEGMENT_WIDTH,
              flex: 1,
            }}
          >
            {segment.cells.map((cell, index) => (
              <Tooltip
                key={index}
                title={cell.tooltip}
                placement='top'
                arrow
                PopperProps={{
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      maxWidth: 'none',
                      p: 3,
                    },
                    '& .MuiTooltip-tooltip,.MuiTooltip-arrow:before': {
                      backgroundColor: '#3B4559',
                      boxShadow: 1,
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    background: 'transparent',
                    flex: 1,
                    minWidth: 0,
                    py: 2,
                    px: 2,
                  }}
                >
                  <Typography
                    align='center'
                    fontSize={14}
                    fontWeight={500}
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {cell.label}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
        );
      })}
    </Box>
  );
};
